import Image from "next/image"
import { AiFillCheckCircle } from "react-icons/ai"
import { HiOutlineArrowLongRight } from "react-icons/hi2"
import PrimaryButton from "../../components/buttons/PrimaryButton"
import ExpenseCard from "../../components/cards/ExpenseCard"
import { IItemTransport } from "../../constants/cost-to-ship/itemTransportsJson"
import formatNumber from "../../util/formatNumber"
import * as config from "../../util/config"

interface IItemTransportCostProps {
  itemTransports: Array<IItemTransport>
  metroName: string
  stateCode: string
}

export default function ItemTransportCost({
  itemTransports,
  metroName,
  stateCode,
}: IItemTransportCostProps) {
  const factors = [
    {
      category: "Vehicles",
      title: "Cost to ship a car",
      pricePerMile: "$1.00",
      description: `A host of factors determine how much it’ll cost to ship your vehicle to the ${metroName} area. This includes its size and condition, the time of year, pickup and drop-off locations, travel distance, whether you use an open or enclosed trailer and mode of transport.`,
      examples:
        "Cars/light trucks, RVs, camper trailers, oversized/lifted trucks, antique/special care vehicles, and others.",
    },
    {
      category: "Motorcycles",
      title: "Cost to ship a motorcycle",
      pricePerMile: "$0.69",
      description:
        "The origin state and transport distance will impact the cost of shipping your motorcycle. An enclosed trailer will protect your bike much better, but an open trailer costs significantly less.",
      examples:
        "Racing bikes, dirt bikes, scooters, cruising bikes/choppers, ATVs, 3-wheelers, and others.",
    },
    {
      category: "Boats",
      title: "Cost to ship a boat",
      pricePerMile: "$1.37",
      description:
        "Your boat is an investment – finding safe transportation should be too. The cost will vary based on your boat’s measurements, the season, and other factors, such as the mode and method of shipping.",
      examples: "Power/motorboats, fishing boats, sailboats, and others.",
    },
    {
      category: "Freight",
      title: "Cost to ship freight",
      pricePerMile: "$1.14",
      description: `Numerous variables determine the cost of freight shipping to the ${metroName} metropolitan area, such as how far it needs to go, its density or volume, whether you need extra accessories and the type of carrier you choose.`,
      examples: "Less than a truckload, or a full truckload.",
    },
    {
      category: "Household Items",
      title: "Cost to ship household items",
      pricePerMile: "$0.70",
      description: `You can use various options to move your household items to the ${metroName} area. The most common choices include renting a van and doing it yourself, hiring a furniture-moving company and utilizing peer-to-peer platforms like CitizenShipper to connect with qualified experts.`,
      examples: "Furniture, kitchen appliances, and boxes.",
    },
    {
      category: "Apartment and House Moves",
      title: "Cost to move",
      pricePerMile: "$1.32",
      description: `Moving homes is a hassle. Transporting your belongings doesn’t have to be. You can rent a van and move everything yourself or hire a company. Using professional movers will make the transition to the ${metroName} area less stressful and help prevent damage.`,
      examples:
        "1 bedroom apartments, 2 bedroom apartments, 3 bedroom house, 4 bedroom house, and a 5 bedroom house.",
    },
    {
      category: "Heavy Equipment",
      title: "Cost to ship heavy equipment",
      pricePerMile: "$1.06",
      description:
        "The size of your equipment will directly affect the shipping cost. Flatbed hauling is the standard solution and sufficient for most heavy equipment transports.",
      examples:
        "Tractors, excavators, backhoe loaders, skid steer loaders, forklift/telehandlers, and others.",
    },
    {
      category: "Local Errands",
      title: "Cost of running errands",
      pricePerMile: "$0.70",
      description: `The average cost of hiring an errand service in ${metroName} is around $30 per hour. Depending on the type of service you need, when you need it and how far the driver travels, you can spend as little as $15 per hour or as much as $100.`,
      examples:
        "Grocery and meal delivery, office supplies shopping, sending and receiving packages and moving services.",
    },
    {
      category: "Special Care",
      title: "Cost to ship fragile items",
      pricePerMile: "$1.52",
      description:
        "Transporting fragile items requires extra attention and care. The travel distance and mode and method of transport will determine the cost. CitizenShipper can connect you with qualified drivers who specialize in safely handling hard-to-ship items.",
      examples:
        "Antiques, heirlooms, musical instruments, artwork, wine & spirits and other fragile items.",
    },
  ]

  const host = config.getWebsiteHost()
  return (
    <section className="w-full bg-blue16">
      <header className="page-padding pb-11">
        <h2 className="max-w-2xl mb-4 mx-auto text-gray8 text-center text-4xl font-montserrat font-semibold">
          What is the cost to ship your precious items to {metroName},{" "}
          {stateCode}?
        </h2>
        <p className="max-w-2xl mx-auto text-center text-xl text-blue5">
          What you're transporting to {metroName} and how you choose to do it
          will determine the cost. Check out the examples and breakdown below.
        </p>
      </header>
      <div className="relative header-padding py-0 grid grid-cols-1 md:grid-cols-2 gap-10">
        {itemTransports[0] && (
          <figure className="relative w-full overflow-hidden h-52 lg:h-[500px]">
            <Image
              className="rounded-2xl w-full h-52 object-cover drop-shadow-xl lg:h-[500px]"
              src={itemTransports[0].image_url[0]}
              alt="cost to ship"
              width={350}
              height={480}
            />
            <figcaption className="absolute text-white bottom-2 left-2 md:bottom-11 md:left-11">
              <p className="w-fit rounded-full bg-blue7 font-montserrat font-semibold lg:mb-4 lg:py-3 lg:px-6 lg:text-3xl">
                ${formatNumber(itemTransports[0].winning_bid_amount)}
              </p>
              <p className="mb-1 flex items-center justify-center font-montserrat lg:text-2xl">
                {itemTransports[0].city_pickup},{" "}
                {itemTransports[0].state_code_pickup}{" "}
                <HiOutlineArrowLongRight className="mx-4 w-8 h-auto" />{" "}
                <strong className="font-semibold">
                  {itemTransports[0].city_deliver},{" "}
                  {itemTransports[0].state_code_deliver}
                </strong>
              </p>
              <p className="text-lg">
                {formatNumber(itemTransports[0].distance_miles)} mi
              </p>
            </figcaption>
          </figure>
        )}
        {itemTransports[1] && (
          <figure className="relative w-full overflow-hidden h-52 lg:h-[500px]">
            <Image
              className="rounded-2xl w-full h-52 object-cover drop-shadow-xl lg:h-[500px]"
              src={itemTransports[1].image_url[0]}
              alt="animal shipping"
              width={350}
              height={480}
            />
            <figcaption className="absolute text-white bottom-2 left-2 md:bottom-11 md:left-11">
              <p className="w-fit rounded-full bg-blue7 font-montserrat font-semibold lg:mb-4 lg:py-3 lg:px-6 lg:text-3xl">
                ${formatNumber(itemTransports[1].winning_bid_amount)}
              </p>
              <p className="mb-1 flex items-center justify-center font-montserrat lg:text-2xl">
                {itemTransports[1].city_pickup},{" "}
                {itemTransports[1].state_code_pickup}{" "}
                <HiOutlineArrowLongRight className="mx-4 w-8 h-auto" />{" "}
                <strong className="font-semibold">
                  {itemTransports[1].city_deliver},{" "}
                  {itemTransports[1].state_code_deliver}
                </strong>
              </p>
              <p className="text-lg">
                {formatNumber(itemTransports[1].distance_miles)} mi
              </p>
            </figcaption>
          </figure>
        )}
      </div>
      <article className="page-padding grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {factors.map((factor, i) => (
          <ExpenseCard {...factor} key={`itemTransportCost/expenseCard${i}`} />
        ))}
        <section className="col-span-full flex flex-col items-center justify-center">
          <PrimaryButton
            onClick={() => window.location.assign(`${host}/shipments/new`)}
          >
            Calculate Your Estimate Now
          </PrimaryButton>
          <p className="mt-2 w-fit flex items-center">
            <AiFillCheckCircle className="inline-block fill-green5 text-xl mr-2" />
            Fast and easy{" "}
            <AiFillCheckCircle className="inline-block fill-green5 text-xl mx-2" />
            Only pay when you book
          </p>
        </section>
      </article>
    </section>
  )
}
